import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Users, CurrencyDollar, TrendUp, Lightning, MagnifyingGlass,
  CaretLeft, CaretRight, CircleNotch, Coins, VideoCamera, Image,
  Rocket, FilmStrip, FilmReel, ArrowUp, ArrowDown, Minus, DownloadSimple,
} from "@phosphor-icons/react";
import { API } from "@/lib/api";
import ProviderSpendWidget from "@/components/ProviderSpendWidget";

const StatCard = ({ icon: Icon, label, value, sub, color = "#E91E8C" }) => (
  <div className="bg-white rounded-xl border border-zinc-200 p-5 hover:shadow-sm transition-all" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">{label}</p>
        <p className="text-2xl font-bold text-zinc-900 mt-1">{value}</p>
        {sub && <p className="text-xs text-zinc-500 mt-0.5">{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon size={22} weight="duotone" style={{ color }} />
      </div>
    </div>
  </div>
);

const GenBar = ({ label, completed, total, color }) => {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-zinc-600 w-32 truncate">{label}</span>
      <div className="flex-1 h-2.5 bg-zinc-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs text-zinc-500 w-20 text-right">{completed}/{total}</span>
    </div>
  );
};

// ──── Overview Tab ────
const OverviewTab = ({ data }) => {
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const runBackfill = async () => {
    if (!window.confirm("Copy all AI Twin images currently on disk into MongoDB so they survive a redeploy? Safe to run multiple times.")) return;
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const { data } = await axios.post(`${API}/ai-twin/images/backfill-binary`, {});
      setBackfillResult(data);
    } catch (e) {
      setBackfillResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setBackfilling(false);
    }
  };

  if (!data) return null;
  const { users, revenue, generations } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={users.total} sub={`+${users.new_30d} last 30 days`} color="#6366f1" />
        <StatCard icon={CurrencyDollar} label="Total Revenue" value={`$${revenue.total.toLocaleString()}`} sub={`$${revenue.last_30d.toLocaleString()} last 30d`} color="#10b981" />
        <StatCard icon={TrendUp} label="Active Subs" value={revenue.active_subscriptions} sub={`${revenue.transactions} total transactions`} color="#E91E8C" />
        <StatCard icon={Coins} label="Credits Sold" value={revenue.total_credits_sold.toLocaleString()} color="#f59e0b" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={VideoCamera} label="Auto Create" value={generations.auto_create_videos} color="#8b5cf6" />
        <StatCard icon={Image} label="AI Twin Images" value={generations.ai_twin_images} color="#E91E8C" />
        <StatCard icon={Rocket} label="Bulk Jobs" value={generations.bulk_jobs} color="#06b6d4" />
        <StatCard icon={FilmStrip} label="Lifestyle Videos" value={generations.lifestyle_videos} color="#f97316" />
      </div>

      <div className="bg-gradient-to-br from-[#E91E8C]/5 via-pink-50 to-white border-2 border-[#E91E8C]/20 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E91E8C]/15 flex items-center justify-center flex-shrink-0">
            <Image size={22} weight="duotone" className="text-[#E91E8C]" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-zinc-900">Protect AI Twin images before redeploy</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              Copies every AI Twin image currently on the container disk into MongoDB so they survive the next production redeploy.
              Safe to run multiple times — already-stored images are skipped.
            </p>
            <button
              onClick={runBackfill}
              disabled={backfilling}
              data-testid="admin-backfill-btn"
              className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#E91E8C] text-white text-sm font-semibold hover:bg-[#c91876] disabled:opacity-50"
            >
              {backfilling ? <><CircleNotch size={14} className="animate-spin" /> Backing up…</> : <>Protect my AI Twin images</>}
            </button>
            {backfillResult && (
              <div className="mt-3 text-xs text-zinc-600" data-testid="admin-backfill-result">
                {backfillResult.error ? (
                  <span className="text-rose-600">Error: {backfillResult.error}</span>
                ) : (
                  <>
                    Backed up <strong>{backfillResult.backfilled}</strong> new image(s) ·
                    {" "}Already safe: {backfillResult.skipped_already_had_blob} ·
                    {" "}Missing on disk: {backfillResult.missing_both} ·
                    {" "}Total tracked: {backfillResult.total_db_records}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ──── Users Tab ────
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [adjusting, setAdjusting] = useState(null);
  const [creditAmount, setCreditAmount] = useState(0);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { params: { search, page, limit: 20 } });
      setUsers(res.data.users);
      setMeta({ total: res.data.total, pages: res.data.pages });
    } catch (e) {
      console.error("Failed to fetch users:", e);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const adjustCredits = async (userId) => {
    if (!creditAmount) return;
    try {
      await axios.patch(`${API}/admin/users/${userId}/credits`, { amount: creditAmount });
      setCreditAmount(0);
      setAdjusting(null);
      fetchUsers();
    } catch (e) {
      console.error("Credit adjust failed:", e);
    }
  };

  const downloadFromUrl = async (url, suggestedName) => {
    try {
      const res = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = suggestedName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (e) {
      alert("Download failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const exportAllUsers = () =>
    downloadFromUrl(`${API}/admin/users/export`, `branding-bar-users-${new Date().toISOString().slice(0, 10)}.csv`);

  const exportSingleUser = (u) =>
    downloadFromUrl(
      `${API}/admin/users/${u.user_id}/export`,
      `export-${(u.email || "user").replace(/@/g, "-at-").replace(/\./g, "-")}.csv`
    );

  const softDeleteUser = async (u) => {
    if (!window.confirm(`Soft-delete ${u.email}?\n\nThey'll be logged out immediately, but their data is preserved for 30 days. You can restore them or permanently delete them after that window.`)) return;
    try {
      await axios.post(`${API}/admin/users/${u.user_id}/soft-delete`);
      fetchUsers();
    } catch (e) {
      alert("Soft delete failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const restoreUser = async (u) => {
    try {
      await axios.post(`${API}/admin/users/${u.user_id}/restore`);
      fetchUsers();
    } catch (e) {
      alert("Restore failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const permanentDeleteUser = async (u) => {
    const confirmText = u.email;
    const typed = window.prompt(
      `⚠️ PERMANENTLY DELETE ${u.email}\n\nThis wipes ALL their content (videos, twins, vault, jobs, brand KB, BYOK keys) across every collection. This CANNOT be undone.\n\nType the user's email to confirm:`
    );
    if (typed !== confirmText) {
      if (typed !== null) alert("Email did not match — deletion cancelled.");
      return;
    }
    try {
      const res = await axios.delete(`${API}/admin/users/${u.user_id}`);
      alert(`Deleted. Cascade summary:\n${JSON.stringify(res.data.cascade || {}, null, 2)}`);
      fetchUsers();
    } catch (e) {
      alert("Permanent delete failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name, email, or brand..."
            className="input-field pl-9 text-sm"
            data-testid="admin-user-search"
          />
        </div>
        <span className="text-sm text-zinc-500">{meta.total} user{meta.total !== 1 ? "s" : ""}</span>
        <button
          onClick={exportAllUsers}
          className="ml-auto flex items-center gap-1.5 text-xs px-3 py-2 border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-700 font-medium"
          data-testid="admin-export-users-csv"
        >
          <DownloadSimple size={14} weight="bold" />
          Export users CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="border-b border-zinc-100 bg-zinc-50/50">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400">User</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400">Brand</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400">Plan</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400">Credits</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400">Joined</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400">Last Active</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-zinc-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8"><CircleNotch size={24} className="animate-spin text-zinc-300 mx-auto" /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-zinc-400">No users found</td></tr>
              ) : users.map((u) => (
                <tr key={u.user_id} className="border-b border-zinc-50 hover:bg-zinc-50/50" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-zinc-200 flex items-center justify-center text-[10px] font-bold text-zinc-500">
                          {(u.name || u.email || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-zinc-900 text-sm">{u.name || "—"}</p>
                        <p className="text-xs text-zinc-400">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600">{u.brand_name || <span className="text-zinc-300">—</span>}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      u.role === "admin" ? "bg-[#E91E8C]/10 text-[#E91E8C]" :
                      u.subscription_tier && u.subscription_tier !== "free" ? "bg-emerald-50 text-emerald-600" :
                      "bg-zinc-100 text-zinc-500"
                    }`}>
                      {u.role === "admin" ? "Admin" : u.subscription_tier || u.plan || "free"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {u.credits === -1 ? <span className="text-[#E91E8C]">Unlimited</span> : u.credits}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-zinc-500 text-xs">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {adjusting === u.user_id ? (
                      <div className="flex items-center gap-1.5 justify-end">
                        <input
                          type="number"
                          value={creditAmount}
                          onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border rounded-lg text-xs text-center"
                          placeholder="+/- credits"
                          data-testid={`credit-input-${u.user_id}`}
                        />
                        <button onClick={() => adjustCredits(u.user_id)} className="text-xs px-2 py-1 bg-[#E91E8C] text-white rounded-lg hover:bg-[#d4177d]" data-testid={`credit-save-${u.user_id}`}>Save</button>
                        <button onClick={() => { setAdjusting(null); setCreditAmount(0); }} className="text-xs px-2 py-1 text-zinc-400 hover:text-zinc-600">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {u.deleted_at && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium" title={`Soft-deleted at ${u.deleted_at}`}>
                            Pending purge
                          </span>
                        )}
                        <button
                          onClick={() => exportSingleUser(u)}
                          className="text-xs px-2 py-1 border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-600"
                          title="Export this user's data as CSV"
                          data-testid={`admin-export-user-${u.user_id}`}
                        >
                          Export
                        </button>
                        <button
                          onClick={() => setAdjusting(u.user_id)}
                          className="text-xs px-2.5 py-1 border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-600"
                          data-testid={`adjust-credits-${u.user_id}`}
                        >
                          Credits
                        </button>
                        {u.deleted_at ? (
                          <>
                            <button
                              onClick={() => restoreUser(u)}
                              className="text-xs px-2 py-1 border border-emerald-200 bg-emerald-50 rounded-lg hover:bg-emerald-100 text-emerald-700 font-medium"
                              data-testid={`admin-restore-${u.user_id}`}
                            >
                              Restore
                            </button>
                            <button
                              onClick={() => permanentDeleteUser(u)}
                              className="text-xs px-2 py-1 border border-red-300 bg-red-50 rounded-lg hover:bg-red-100 text-red-700 font-medium"
                              data-testid={`admin-permadelete-${u.user_id}`}
                            >
                              Delete
                            </button>
                          </>
                        ) : u.role !== "admin" ? (
                          <button
                            onClick={() => softDeleteUser(u)}
                            className="text-xs px-2 py-1 border border-red-200 rounded-lg hover:bg-red-50 text-red-600"
                            data-testid={`admin-softdelete-${u.user_id}`}
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {meta.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} className="p-2 rounded-lg border border-zinc-200 hover:bg-zinc-50 disabled:opacity-30" data-testid="prev-page">
            <CaretLeft size={16} />
          </button>
          <span className="text-sm text-zinc-500">Page {page} of {meta.pages}</span>
          <button onClick={() => setPage(Math.min(meta.pages, page + 1))} disabled={page >= meta.pages} className="p-2 rounded-lg border border-zinc-200 hover:bg-zinc-50 disabled:opacity-30" data-testid="next-page">
            <CaretRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

// ──── Revenue & Usage Tab ────
const RevenueTab = () => {
  const [revenue, setRevenue] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [revRes, useRes] = await Promise.all([
          axios.get(`${API}/admin/revenue`, { params: { days: 30 } }),
          axios.get(`${API}/admin/usage`),
        ]);
        setRevenue(revRes.data);
        setUsage(useRes.data);
      } catch (e) {
        console.error("Failed to load revenue/usage:", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><CircleNotch size={28} className="animate-spin text-zinc-300" /></div>;

  const genStats = usage?.generation_stats || {};

  return (
    <div className="space-y-6">
      {/* Generation Stats */}
      <div className="bg-white rounded-xl border border-zinc-200 p-5">
        <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Generation Stats (All Time)</h3>
        <div className="space-y-3">
          <GenBar label="Auto Create" completed={genStats.auto_create?.completed || 0} total={genStats.auto_create?.total || 0} color="#8b5cf6" />
          <GenBar label="Bulk Create" completed={genStats.bulk_create?.completed || 0} total={genStats.bulk_create?.total || 0} color="#06b6d4" />
          <GenBar label="Lifestyle Video" completed={genStats.lifestyle_video?.completed || 0} total={genStats.lifestyle_video?.total || 0} color="#f97316" />
          <GenBar label="AI Twin" completed={genStats.ai_twin?.completed || 0} total={genStats.ai_twin?.total || 0} color="#E91E8C" />
          {genStats.series?.total > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-600 w-32">Series</span>
              <span className="text-xs text-zinc-500">{genStats.series.total} created</span>
            </div>
          )}
        </div>
      </div>

      {/* Daily Revenue Mini-Chart */}
      {revenue?.daily?.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Daily Revenue (Last 30 Days)</h3>
          <div className="flex items-end gap-1 h-32">
            {revenue.daily.map((d) => {
              const maxRev = Math.max(...revenue.daily.map((r) => r.revenue), 1);
              const h = Math.max(4, (d.revenue / maxRev) * 100);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
                    ${d.revenue} — {d.date.slice(5)}
                  </div>
                  <div className="w-full rounded-t bg-emerald-400 hover:bg-emerald-500 transition-colors" style={{ height: `${h}%` }} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Revenue by Type */}
      {revenue?.by_type?.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Revenue by Type</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {revenue.by_type.map((t) => (
              <div key={t.type} className="bg-zinc-50 rounded-lg p-3">
                <p className="text-xs text-zinc-400 capitalize">{t.type}</p>
                <p className="text-lg font-bold text-zinc-900">${t.revenue}</p>
                <p className="text-xs text-zinc-500">{t.count} transactions</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Signup Trend */}
      {usage?.signups_daily?.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Signups (Last 30 Days)</h3>
          <div className="flex items-end gap-1 h-24">
            {usage.signups_daily.map((d) => {
              const maxC = Math.max(...usage.signups_daily.map((r) => r.count), 1);
              const h = Math.max(8, (d.count / maxC) * 100);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center group relative">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-zinc-800 text-white text-[10px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none z-10">
                    {d.count} — {d.date.slice(5)}
                  </div>
                  <div className="w-full rounded-t bg-indigo-400 hover:bg-indigo-500 transition-colors" style={{ height: `${h}%` }} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      {revenue?.recent_transactions?.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Recent Transactions</h3>
          <div className="space-y-2">
            {revenue.recent_transactions.map((tx) => (
              <div key={tx.session_id} className="flex items-center justify-between py-2 border-b border-zinc-50 last:border-0">
                <div>
                  <p className="text-sm font-medium text-zinc-800">{tx.user_id}</p>
                  <p className="text-xs text-zinc-400">{tx.type || "credits"} — {tx.pack_id || tx.plan_id || "—"}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-emerald-600">+${tx.amount}</p>
                  <p className="text-xs text-zinc-400">{tx.paid_at ? new Date(tx.paid_at).toLocaleDateString() : "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Most Active Users */}
      {usage?.active_users?.length > 0 && (
        <div className="bg-white rounded-xl border border-zinc-200 p-5">
          <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-400 mb-4">Recently Active Users</h3>
          <div className="space-y-2">
            {usage.active_users.map((u) => (
              <div key={u.email} className="flex items-center justify-between py-2 border-b border-zinc-50 last:border-0">
                <div>
                  <p className="text-sm font-medium text-zinc-800">{u.name || u.email}</p>
                  <p className="text-xs text-zinc-400">{u.brand_name || u.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-zinc-700">{u.credits} credits</p>
                  <p className="text-xs text-zinc-400">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ──── Revenue Snapshot Panel ────
const Delta = ({ current, prev, label = "vs last mo", unit = "" }) => {
  if (prev === null || prev === undefined) return <span className="text-[11px] text-zinc-400">—</span>;
  const diff = current - prev;
  const pct = prev ? Math.round((diff / prev) * 1000) / 10 : null;
  const up = diff > 0;
  const flat = diff === 0;
  const Icon = flat ? Minus : up ? ArrowUp : ArrowDown;
  const color = flat ? "text-zinc-500" : up ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${color}`}>
      <Icon size={10} weight="bold" />
      {pct === null ? `${unit}${Math.abs(diff).toLocaleString()}` : `${Math.abs(pct)}%`}
      <span className="text-zinc-400 font-normal ml-0.5">{label}</span>
    </span>
  );
};

const MiniBarChart = ({ months, valueKey, color = "#E91E8C" }) => {
  const values = months.map((m) => Number(m[valueKey] || 0));
  const max = Math.max(1, ...values);
  return (
    <div className="flex items-end gap-1 h-16" data-testid={`snapshot-chart-${valueKey}`}>
      {months.map((m, i) => {
        const v = Number(m[valueKey] || 0);
        const h = Math.max(2, Math.round((v / max) * 60));
        return (
          <div key={m.month_key} className="flex-1 flex flex-col items-center gap-0.5 group">
            <div className="text-[9px] font-bold opacity-0 group-hover:opacity-100 transition" style={{ color }}>
              {v > 0 ? (valueKey.includes("usd") || valueKey === "mrr" ? `$${v.toLocaleString()}` : v) : ""}
            </div>
            <div
              className="w-full rounded-t"
              style={{
                height: `${h}px`,
                background: m.empty ? "#e4e4e7" : color,
                opacity: i === months.length - 1 ? 1 : 0.6 + (i / months.length) * 0.4,
              }}
              title={`${m.month_key}: ${v}`}
            />
            <span className="text-[9px] text-zinc-400">{m.month_key.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
};

const ComparisonCard = ({ history, cmpA, cmpB, setCmpA, setCmpB }) => {
  const rowA = history.find((m) => m.month_key === cmpA);
  const rowB = history.find((m) => m.month_key === cmpB);
  if (!rowA || !rowB) return null;

  const label = (k) => new Date(k + "-15").toLocaleDateString(undefined, { month: "short", year: "numeric" });

  const rows = [
    { key: "mrr", label: "MRR", unit: "$" },
    { key: "active_subscriptions", label: "Active subs", unit: "" },
    { key: "new_subscriptions_this_month", label: "New subs", unit: "" },
    { key: "churned_this_month", label: "Churned", unit: "" },
    { key: "overage_billed_this_month_usd", label: "Overage billed", unit: "$" },
  ];

  return (
    <div className="p-4 bg-white rounded-xl border border-zinc-200 mt-4" data-testid="revenue-comparison-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-[10px] uppercase font-bold text-zinc-500 tracking-widest">Compare any two months</p>
          <p className="text-sm text-zinc-700 mt-0.5">Handy for board updates and quarterly reviews.</p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <select
            value={cmpA || ""}
            onChange={(e) => setCmpA(e.target.value)}
            className="border border-zinc-200 rounded-lg px-2 py-1.5 bg-white text-zinc-700"
            data-testid="comparison-select-a"
          >
            {history.map((m) => (<option key={m.month_key} value={m.month_key}>{label(m.month_key)}</option>))}
          </select>
          <span className="text-zinc-400 text-xs">vs</span>
          <select
            value={cmpB || ""}
            onChange={(e) => setCmpB(e.target.value)}
            className="border border-zinc-200 rounded-lg px-2 py-1.5 bg-white text-zinc-700"
            data-testid="comparison-select-b"
          >
            {history.map((m) => (<option key={m.month_key} value={m.month_key}>{label(m.month_key)}</option>))}
          </select>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase text-zinc-400 border-b border-zinc-100">
            <th className="py-1.5 font-semibold">Metric</th>
            <th className="py-1.5 font-semibold text-right">{label(cmpA)}</th>
            <th className="py-1.5 font-semibold text-right">{label(cmpB)}</th>
            <th className="py-1.5 font-semibold text-right">Δ</th>
            <th className="py-1.5 font-semibold text-right">% change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, label: l, unit }) => {
            const a = Number(rowA[key] || 0);
            const b = Number(rowB[key] || 0);
            const diff = b - a;
            const pct = a !== 0 ? Math.round((diff / a) * 1000) / 10 : null;
            const up = diff > 0;
            const flat = diff === 0;
            const Icon = flat ? Minus : up ? ArrowUp : ArrowDown;
            const color = flat ? "text-zinc-500" : up ? "text-emerald-600" : "text-red-600";
            const fmt = (v) => unit === "$" ? `$${v.toLocaleString(undefined, {minimumFractionDigits:2})}` : v.toLocaleString();
            return (
              <tr key={key} className="border-b border-zinc-50" data-testid={`comparison-row-${key}`}>
                <td className="py-2 text-zinc-700">{l}</td>
                <td className="py-2 text-right font-mono text-zinc-900">{fmt(a)}</td>
                <td className="py-2 text-right font-mono text-zinc-900">{fmt(b)}</td>
                <td className={`py-2 text-right font-mono font-bold ${color}`}>
                  {up ? "+" : diff < 0 ? "" : "±"}{fmt(Math.abs(diff))}
                </td>
                <td className={`py-2 text-right ${color}`}>
                  <span className="inline-flex items-center gap-0.5 font-bold text-xs">
                    <Icon size={10} weight="bold" />
                    {pct === null ? "—" : `${Math.abs(pct)}%`}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const RevenueSnapshotPanel = () => {
  const [snap, setSnap] = useState(null);
  const [history, setHistory] = useState(null);
  const [err, setErr] = useState(null);
  // Compare-any-two-months state
  const [cmpA, setCmpA] = useState(null); // month_key
  const [cmpB, setCmpB] = useState(null); // month_key

  useEffect(() => {
    (async () => {
      try {
        const [s, h] = await Promise.all([
          axios.get(`${API}/admin/billing/snapshot`),
          axios.get(`${API}/admin/billing/snapshot-history?months=12`),
        ]);
        setSnap(s.data);
        setHistory(h.data.months || []);
        // Default comparison: latest vs previous month.
        const months = h.data.months || [];
        if (months.length >= 2) {
          setCmpA(months[months.length - 2].month_key);
          setCmpB(months[months.length - 1].month_key);
        }
      } catch (e) {
        setErr(e.response?.data?.detail || "Failed to load snapshot");
      }
    })();
  }, []);

  if (err) return <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{err}</div>;
  if (!snap) return <div className="flex items-center justify-center py-8"><CircleNotch size={22} className="animate-spin text-[#E91E8C]" /></div>;

  const monthLabel = new Date(snap.month_key + "-15").toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const downloadCSV = () => {
    if (!history || history.length === 0) return;
    const header = ["month", "mrr_usd", "active_subs", "new_subs", "churned", "overage_billed_usd"];
    const rows = history.map((m) => [
      m.month_key,
      m.mrr,
      m.active_subscriptions,
      m.new_subscriptions_this_month,
      m.churned_this_month,
      m.overage_billed_this_month_usd,
    ]);
    const csv = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-snapshot-${snap.month_key}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const prev = snap.prev_snapshot;

  return (
    <div className="bg-gradient-to-br from-[#E91E8C]/5 via-white to-white rounded-2xl border-2 border-[#E91E8C]/20 p-5 mb-4" data-testid="revenue-snapshot-panel">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase font-bold tracking-widest text-[#E91E8C]">Revenue Snapshot</p>
          <h3 className="text-xl font-bold text-zinc-900">{monthLabel} · at a glance</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Auto-refreshed every time you open this tab. Historical months are locked once the calendar month rolls over.</p>
        </div>
        <button
          onClick={downloadCSV}
          className="text-xs font-semibold text-zinc-700 hover:text-[#E91E8C] flex items-center gap-1 bg-white border border-zinc-200 px-3 py-1.5 rounded-lg"
          data-testid="snapshot-download-csv"
        >
          <DownloadSimple size={14} /> Export 12-mo CSV
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="p-3 rounded-xl bg-white border border-zinc-100" data-testid="snapshot-mrr">
          <p className="text-[10px] uppercase font-bold text-zinc-400">MRR</p>
          <p className="text-2xl font-bold text-zinc-900">${snap.mrr.toLocaleString(undefined,{minimumFractionDigits:2})}</p>
          <Delta current={snap.mrr} prev={prev?.mrr ?? null} />
        </div>
        <div className="p-3 rounded-xl bg-white border border-zinc-100" data-testid="snapshot-net-new">
          <p className="text-[10px] uppercase font-bold text-zinc-400">Net new subs</p>
          <p className="text-2xl font-bold text-zinc-900">{snap.net_new_this_month >= 0 ? "+" : ""}{snap.net_new_this_month}</p>
          <span className="text-[11px] text-zinc-500">{snap.new_subscriptions_this_month} new · {snap.churned_this_month} churned</span>
        </div>
        <div className="p-3 rounded-xl bg-white border border-zinc-100" data-testid="snapshot-overage">
          <p className="text-[10px] uppercase font-bold text-zinc-400">Overage billed</p>
          <p className="text-2xl font-bold text-zinc-900">${snap.overage_billed_this_month_usd.toLocaleString(undefined,{minimumFractionDigits:2})}</p>
          <Delta current={snap.overage_billed_this_month_usd} prev={prev?.overage_billed_this_month_usd ?? null} unit="$" />
        </div>
        <div className="p-3 rounded-xl bg-white border border-zinc-100" data-testid="snapshot-projected">
          <p className="text-[10px] uppercase font-bold text-zinc-400">Projected month total</p>
          <p className="text-2xl font-bold text-emerald-700">${snap.total_revenue_this_month_projected_usd.toLocaleString(undefined,{minimumFractionDigits:2})}</p>
          <span className="text-[11px] text-zinc-500">MRR + overage billed + unbilled accruing</span>
        </div>
      </div>

      {history && history.length > 0 && (
        <>
          {/* Compare any two months */}
          <ComparisonCard history={history} cmpA={cmpA} cmpB={cmpB} setCmpA={setCmpA} setCmpB={setCmpB} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 bg-white rounded-xl border border-zinc-100" data-testid="snapshot-chart-card-mrr">
            <p className="text-[10px] uppercase font-bold text-zinc-500 mb-2">MRR — 12 month trend</p>
            <MiniBarChart months={history} valueKey="mrr" color="#E91E8C" />
          </div>
          <div className="p-3 bg-white rounded-xl border border-zinc-100" data-testid="snapshot-chart-card-subs">
            <p className="text-[10px] uppercase font-bold text-zinc-500 mb-2">New vs churned subs</p>
            <MiniBarChart months={history} valueKey="new_subscriptions_this_month" color="#10b981" />
          </div>
        </div>
        </>
      )}

      {snap.past_due > 0 && (
        <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="snapshot-past-due-alert">
          ⚠ {snap.past_due} subscription{snap.past_due === 1 ? "" : "s"} in <strong>past_due</strong> right now. They&apos;re in the 24h grace window before their access is paused — consider a check-in email.
        </div>
      )}
    </div>
  );
};

// ──── Billing (MRR) Tab ────
const BillingTab = () => {
  const [mrr, setMrr] = useState(null);
  const [subs, setSubs] = useState(null);
  const [overage, setOverage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, s, o] = await Promise.all([
        axios.get(`${API}/admin/billing/mrr`),
        axios.get(`${API}/admin/billing/subscriptions?limit=100`),
        axios.get(`${API}/admin/billing/overage`),
      ]);
      setMrr(m.data);
      setSubs(s.data);
      setOverage(o.data);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to load billing data");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const billOneUser = async (uid) => {
    if (!window.confirm("Bill this user's outstanding overage immediately via Stripe?")) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/billing/bill-overage/${uid}`);
      setMsg(r.data.billed ? `Billed $${r.data.amount_usd.toFixed(2)} to ${uid}` : `Skipped: ${r.data.reason}`);
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Billing failed");
    } finally { setBusy(false); }
  };

  const billAll = async () => {
    if (!window.confirm(`Bill outstanding overage for ALL ${overage?.total_users || 0} user(s) — projected $${overage?.total_projected_charge_usd?.toFixed(2) || 0}?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await axios.post(`${API}/admin/billing/bill-all-overage`);
      setMsg(`Billed ${r.data.billed}/${r.data.attempted} users — $${r.data.total_billed_usd.toFixed(2)}`);
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Bulk billing failed");
    } finally { setBusy(false); }
  };

  if (!mrr) return <div className="flex items-center justify-center py-20"><CircleNotch size={28} className="animate-spin text-[#E91E8C]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-billing-tab">
      <RevenueSnapshotPanel />
      <ProviderSpendWidget />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CurrencyDollar} label="MRR" value={`$${mrr.mrr.toLocaleString(undefined, {minimumFractionDigits:2})}`} sub="Recurring monthly" color="#10b981" />
        <StatCard icon={TrendUp} label="ARR (proj.)" value={`$${mrr.arr.toLocaleString(undefined, {minimumFractionDigits:2})}`} sub="MRR × 12" color="#E91E8C" />
        <StatCard icon={Users} label="Active Subs" value={mrr.active_subscriptions} sub={`${mrr.canceled} canceled · ${mrr.past_due} past due`} color="#6366f1" />
        <StatCard icon={Lightning} label="Unbilled overage" value={overage?.total_overage_units || 0} sub={`~$${(overage?.total_projected_charge_usd || 0).toLocaleString(undefined, {minimumFractionDigits:2})}`} color="#f59e0b" />
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 p-5" data-testid="admin-mrr-by-tier">
        <h3 className="font-semibold text-zinc-900 mb-4">MRR by tier</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(mrr.by_tier).map(([tier, v]) => (
            <div key={tier} className="p-3 rounded-lg bg-zinc-50 border border-zinc-100" data-testid={`mrr-tier-${tier}`}>
              <p className="text-[10px] uppercase font-bold text-zinc-500">{tier}</p>
              <p className="text-lg font-bold text-zinc-900">${v.mrr.toLocaleString(undefined,{minimumFractionDigits:2})}</p>
              <p className="text-xs text-zinc-500">{v.count} × ${v.price}/mo</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 p-5" data-testid="admin-overage-list">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-zinc-900">Outstanding overage</h3>
          {(overage?.total_users || 0) > 0 && (
            <button
              onClick={billAll}
              disabled={busy}
              data-testid="bill-all-overage-btn"
              className="btn-primary text-xs px-4 py-2 disabled:opacity-50"
            >Bill all now</button>
          )}
        </div>
        {msg && <p className="text-xs mb-2 text-emerald-700 font-semibold">{msg}</p>}
        {(overage?.users || []).length === 0 ? (
          <p className="text-sm text-zinc-500">No unbilled overage — everyone&apos;s within their monthly cap.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-zinc-400 border-b border-zinc-100">
                <th className="py-2">User</th>
                <th className="py-2">Tier</th>
                <th className="py-2 text-right">Overage</th>
                <th className="py-2 text-right">Projected</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {overage.users.map((u) => (
                <tr key={u.user_id} className="border-b border-zinc-50" data-testid={`overage-row-${u.user_id}`}>
                  <td className="py-2 text-zinc-900">{u.email}</td>
                  <td className="py-2 capitalize">{u.subscription_tier}</td>
                  <td className="py-2 text-right">{u.overage_this_period}</td>
                  <td className="py-2 text-right font-semibold text-emerald-700">${u.projected_charge_usd.toFixed(2)}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => billOneUser(u.user_id)}
                      disabled={busy}
                      data-testid={`bill-overage-${u.user_id}`}
                      className="text-xs font-semibold text-[#E91E8C] hover:underline disabled:opacity-50"
                    >Bill now →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 p-5" data-testid="admin-subs-list">
        <h3 className="font-semibold text-zinc-900 mb-4">Active subscriptions ({subs?.total || 0})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-zinc-400 border-b border-zinc-100">
                <th className="py-2">Email</th>
                <th className="py-2">Tier</th>
                <th className="py-2">Status</th>
                <th className="py-2 text-right">$/mo</th>
                <th className="py-2 text-right">Videos used</th>
                <th className="py-2">Renews</th>
              </tr>
            </thead>
            <tbody>
              {(subs?.subscriptions || []).map((s) => {
                const renews = s.period_end ? new Date(s.period_end).toLocaleDateString() : "—";
                return (
                  <tr key={s.user_id} className="border-b border-zinc-50" data-testid={`sub-row-${s.user_id}`}>
                    <td className="py-2 text-zinc-900">{s.email}</td>
                    <td className="py-2 capitalize font-semibold">{s.subscription_tier}{s.pending_tier ? <span className="ml-1 text-[10px] text-amber-600">→ {s.pending_tier}</span> : null}</td>
                    <td className="py-2">
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                        s.subscription_status === "active" ? "bg-emerald-100 text-emerald-700" :
                        s.subscription_status === "past_due" ? "bg-red-100 text-red-700" :
                        "bg-amber-100 text-amber-700"
                      }`}>{s.subscription_status || "active"}</span>
                    </td>
                    <td className="py-2 text-right">${s.monthly_price?.toFixed(2) || "0.00"}</td>
                    <td className="py-2 text-right">{s.videos_used_this_period || 0} / {s.videos_limit || 0}</td>
                    <td className="py-2 text-xs text-zinc-500">{renews}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ──── Main ────
const TABS = [
  { id: "overview", label: "Overview", icon: TrendUp },
  { id: "users", label: "Users", icon: Users },
  { id: "revenue", label: "Revenue & Usage", icon: CurrencyDollar },
  { id: "billing", label: "Billing (MRR)", icon: Coins },
];

const AdminDashboard = () => {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/overview`);
        setOverview(res.data);
      } catch (e) {
        console.error("Failed to load admin overview:", e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <CircleNotch size={32} className="animate-spin text-[#E91E8C]" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Admin Dashboard</h2>
        <p className="text-zinc-500 text-sm mt-1">Platform overview, user management, and revenue analytics</p>
      </div>

      <div className="flex gap-1 bg-zinc-100 p-1 rounded-xl w-fit" data-testid="admin-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.id ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <Icon size={16} weight={tab === t.id ? "fill" : "regular"} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewTab data={overview} />}
      {tab === "users" && <UsersTab />}
      {tab === "revenue" && <RevenueTab />}
      {tab === "billing" && <BillingTab />}
    </div>
  );
};

export default AdminDashboard;
